import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle } from 'lucide-react';
import { TreatmentForm } from '../components/TreatmentForm';
import { PhotoViewer } from '../components/PhotoViewer';
import { Button } from '../components/ui/button';
import type { TreatmentCategory } from '../types';

const categoryLabels: Record<TreatmentCategory, string> = {
  'pestanas': 'Pestañas',
  'lifting': 'Lifting',
  'cejas': 'Cejas',
  'micropigmentacion-cejas': 'Micropigmentación de Cejas',
  'micropigmentacion-labios': 'Micropigmentación de Labios',
  'micropigmentacion-ojos': 'Micropigmentación de Línea de Ojos',
  'manicura': 'Manicura',
  'pedicura': 'Pedicura',
  'tratamientos-faciales': 'Tratamientos Faciales',
  'tinte-cabello': 'Tinte de Cabello',
  'corte-pelo': 'Corte de Pelo'
};

export function NewTreatment() {
  const navigate = useNavigate();
  const { clientId, category, treatmentId } = useParams();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photoBeforePreview, setPhotoBeforePreview] = useState<string>();
  const [photoAfterPreview, setPhotoAfterPreview] = useState<string>();
  const [photoBeforeFile, setPhotoBeforeFile] = useState<File | null>(null);
  const [photoAfterFile, setPhotoAfterFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(treatmentId));
  const [error, setError] = useState<string | null>(null);
  const [resolvedCategory, setResolvedCategory] = useState<TreatmentCategory | undefined>(category as TreatmentCategory | undefined);
  const isEditMode = Boolean(treatmentId);

  const handlePhotoBefore = (file: File) => {
    setPhotoBeforeFile(file);
    setPhotoBeforePreview(URL.createObjectURL(file));
  };

  const handlePhotoAfter = (file: File) => {
    setPhotoAfterFile(file);
    setPhotoAfterPreview(URL.createObjectURL(file));
  };

  useEffect(() => {
    if (!treatmentId) {
      setResolvedCategory(category as TreatmentCategory | undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    fetch(`/server/fichas_cliente_sisters_peluqueros_function/treatment/${encodeURIComponent(treatmentId)}`)
      .then(async response => {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'No se pudo cargar el tratamiento');
        }

        return data as {
          Tratamientos: {
            ROWID: string;
            Specialist: string;
            Categoria: TreatmentCategory;
            FotosAntes?: string | null;
            FotoDespues?: string | null;
            FormData?: string;
          };
        };
      })
      .then(({ Tratamientos: treatment }) => {
        let parsedFormData: Record<string, any> = {};

        try {
          parsedFormData = treatment.FormData ? JSON.parse(treatment.FormData) : {};
        } catch (parseError) {
          console.error('Error al parsear el formulario del tratamiento:', parseError);
        }

        setFormData(parsedFormData);
        setResolvedCategory(treatment.Categoria);
        setPhotoBeforePreview(treatment.FotosAntes || undefined);
        setPhotoAfterPreview(treatment.FotoDespues || undefined);
      })
      .catch(fetchError => {
        console.error('Error al cargar el tratamiento para edición:', fetchError);
        setError(fetchError instanceof Error ? fetchError.message : 'No se pudo cargar el tratamiento');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [category, treatmentId]);

  const serializePhoto = async (file: File | null) => {
    if (!file) {
      return null;
    }

    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        const [, payload = ''] = result.split(',');
        resolve(payload);
      };

      reader.onerror = () => {
        reject(reader.error || new Error('No se pudo leer la imagen'));
      };

      reader.readAsDataURL(file);
    });

    return {
      fileName: file.name,
      mimeType: file.type,
      base64Data,
    };
  };

  const saveTreatment = async (isDraft: boolean) => {
    if (!clientId || !resolvedCategory) {
      throw new Error('Faltan datos de cliente o categoria');
    }

    const [serializedBeforePhoto, serializedAfterPhoto] = await Promise.all([
      serializePhoto(photoBeforeFile),
      serializePhoto(photoAfterFile),
    ]);

    const endpoint = isEditMode
      ? `/server/fichas_cliente_sisters_peluqueros_function/treatment/${encodeURIComponent(treatmentId!)}`
      : `/server/fichas_cliente_sisters_peluqueros_function/client/${encodeURIComponent(clientId)}/new-treatment/${encodeURIComponent(resolvedCategory)}`;

    const response = await fetch(endpoint, {
      method: isEditMode ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        formData,
        photoBefore: serializedBeforePhoto,
        photoAfter: serializedAfterPhoto,
        isDraft
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'No se pudo guardar el tratamiento');
    }

    return data;
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      await saveTreatment(true);
      navigate(`/client/${clientId}`);
    } catch (saveError) {
      console.error('Error al guardar borrador:', saveError);
      setError(saveError instanceof Error ? saveError.message : 'No se pudo guardar el borrador');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinish = async () => {
    try {
      setIsSaving(true);
      await saveTreatment(false);
      navigate(`/client/${clientId}`);
    } catch (saveError) {
      console.error('Error al finalizar tratamiento:', saveError);
      setError(saveError instanceof Error ? saveError.message : 'No se pudo finalizar el tratamiento');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-gray-500">Cargando tratamiento...</div>;
  }

  if (error && !resolvedCategory) {
    return <div className="p-6 text-red-500">{error}</div>;
  }

  if (!resolvedCategory) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/client/${clientId}`)}
              className="text-gray-600 hover:text-[#E91E8C]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl text-[#2d2d2d]">
                {isEditMode ? 'Editar Tratamiento' : 'Nuevo Tratamiento'}
              </h1>
              <p className="text-sm text-gray-600">
                {categoryLabels[resolvedCategory]}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="mb-5">Datos del Tratamiento</h3>
          <TreatmentForm
            category={resolvedCategory}
            formData={formData}
            onChange={setFormData}
          />
        </div>

        <PhotoViewer
          photoBefore={photoBeforePreview}
          photoAfter={photoAfterPreview}
          onUploadBefore={handlePhotoBefore}
          onUploadAfter={handlePhotoAfter}
        />
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex gap-3">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="flex-1 h-12 border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C]/5"
          >
            <Save className="w-5 h-5 mr-2" />
            Guardar Borrador
          </Button>
          <Button
            onClick={handleFinish}
            disabled={isSaving}
            className="flex-1 h-12 bg-[#E91E8C] hover:bg-[#d01a7d] text-white"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Finalizar Tratamiento
          </Button>
        </div>
      </div>

    </div>
  );
}
